import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactNode } from 'react';

type Direction = 'up' | 'down' | 'left' | 'right' | 'none';

type RevealProps = {
  children: ReactNode;
  /** Slide direction before reveal. Default: 'up'. */
  direction?: Direction;
  /** Pixel distance for the slide in. Default: 32. */
  distance?: number;
  /** Delay in ms (useful for staggering grid items). */
  delay?: number;
  /** Transition duration in ms. Default: 700. */
  duration?: number;
  /** Only reveal once (default). Set false to re-hide when scrolled away. */
  once?: boolean;
  /** IntersectionObserver threshold. Default: 0.15. */
  threshold?: number;
  className?: string;
  style?: CSSProperties;
  /** Render tag. Default: 'div'. */
  as?: 'div' | 'section' | 'span' | 'li' | 'article';
};

/**
 * Viewport-triggered reveal animation with fade + directional slide.
 * Respects `prefers-reduced-motion` (instantly visible, no transform).
 */
export default function Reveal({
  children,
  direction = 'up',
  distance = 32,
  delay = 0,
  duration = 700,
  once = true,
  threshold = 0.15,
  className = '',
  style,
  as: Tag = 'div',
}: RevealProps) {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setReducedMotion(true);
      setVisible(true);
      return;
    }

    const el = ref.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            setVisible(true);
            if (once) io.disconnect();
          } else if (!once) {
            setVisible(false);
          }
        }
      },
      { threshold, rootMargin: '0px 0px -8% 0px' },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [once, threshold]);

  const hiddenTransform = (() => {
    switch (direction) {
      case 'up':
        return `translate3d(0, ${distance}px, 0)`;
      case 'down':
        return `translate3d(0, ${-distance}px, 0)`;
      case 'left':
        return `translate3d(${distance}px, 0, 0)`;
      case 'right':
        return `translate3d(${-distance}px, 0, 0)`;
      default:
        return 'translate3d(0, 0, 0)';
    }
  })();

  const mergedStyle: CSSProperties = {
    transitionProperty: 'opacity, transform',
    transitionDuration: `${duration}ms`,
    transitionTimingFunction: 'cubic-bezier(0.22, 1, 0.36, 1)',
    transitionDelay: `${delay}ms`,
    transform: reducedMotion || visible ? 'translate3d(0,0,0)' : hiddenTransform,
    opacity: reducedMotion || visible ? 1 : 0,
    willChange: 'transform, opacity',
    ...style,
  };

  // Cast ref assignment for polymorphic Tag; React handles it fine at runtime.
  return (
    <Tag
      ref={ref as React.MutableRefObject<never>}
      className={className}
      style={mergedStyle}
    >
      {children}
    </Tag>
  );
}
